/** Angular imports */
import { Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
} from '@angular/forms';
import { isEqual } from 'lodash';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    first,
    map,
    Observable,
    of,
    Subscription,
    switchMap,
    timer,
} from 'rxjs';

/** Core imports */
import {
    ApiGetAllResponse,
    ApiUser,
    CreateUserStepsApiService,
    CreateUserStepsService,
    UsersService,
} from '@core';

/** Component imports */
import { Input } from '@ntv360/component-pantry';
import {
    ApiCity,
    AutocompleteComponent,
    AvatarUploaderComponent,
    UiAutocompleteCity,
} from '@shared';

/** Local imports */
import { INPUT_INFORMATION_STEP_LAYOUT } from '@features/users/constants';
import { CreateUserFlowDraft, UserRoles } from '@features/users/interfaces';
import { comparePasswords, formBuilder, Logger, withLoading } from '@shared/utils';

const createNewUserComps = [
    AutocompleteComponent,
    ReactiveFormsModule,
    AvatarUploaderComponent,
    Input,
];

@Component({
    selector: 'app-input-information',
    imports: [createNewUserComps],
    templateUrl: './input-information.component.html',
    styleUrl: './input-information.component.scss',
})
export class InputInformationComponent implements OnInit {
    /** Maximum allowed password length. */
    private static readonly PASSWORD_MAX_LENGTH = 72;
    /** Allowed profile image MIME types. */
    private static readonly ALLOWED_IMAGE_MIME_TYPES = new Set([
        'image/jpeg',
        'image/jpg',
        'image/png',
    ]);
    /**
     * Inject form builder to construct the form
     */
    private fb = inject(FormBuilder);

    /**
     * Inject create user service
     */
    private createUserStepsService = inject(CreateUserStepsService);

    /**
     * Inject create user api service
     */
    private createUserStepsApiService = inject(CreateUserStepsApiService);
    /**
     * Inject users service for server-side email verification via users search API.
     */
    private usersService = inject(UsersService);

    /**
     * JOke lang po ito hehe
     */
    protected cuteCat = 'https://m.media-amazon.com/images/I/61L61gSPJhL.jpg';

    /**
     * Selected user role to be created for dynamic form rendering
     */
    protected selectedUserType = this.createUserStepsService.selectedUserType;

    /**
     * Access draft data from the service.
     */
    protected draftData = this.createUserStepsService.draft;

    /**
     * Local profile picture preview for cross-step display.
     */
    protected profilePicturePreview = this.createUserStepsService.profilePicturePreview;

    /**
     * Loading states
     */
    protected isLoadingCities = signal<boolean>(false);
    /** Controls visibility for invalid file type modal. */
    protected showInvalidFileModal = signal<boolean>(false);

    /**
     * City list
     */
    protected cityList = signal<UiAutocompleteCity[]>([]);

    /** Holds the subscription reference for search city to cancel any in-flight net request when a new search value is emitted */
    private searchCitySubscriptionRef?: Subscription;

    /** Stores the last search term to prevent redundant API calls when opening the autocomplete or typing the same value */
    private lastSearchTerm = signal<string>('');

    constructor() {
        /**
         * Real-time synchronization effect.
         * Listen to form value changes and update the draft service automatically.
         * We use debounceTime to avoid excessive signal updates during rapid typing.
         */
        effect((onCleanup) => {
            const form = this.form();
            // Use getRawValue(): disabled State/Region are omitted from FormGroup.value, so syncing
            // only `value` would leave stale state/region in the draft and the draft→form effect
            // would repopulate the inputs after clearing City.
            const sub = form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
                this.createUserStepsService.patchDraftStep(
                    this.selectedUserType()?.id as Exclude<UserRoles, 'none'>,
                    'input-information' as keyof CreateUserFlowDraft[Exclude<UserRoles, 'none'>],
                    form.getRawValue() as CreateUserFlowDraft[Exclude<
                        UserRoles,
                        'none'
                    >]['input-information'],
                );

                if (!form.valid) {
                    this.createUserStepsService.setInvalidStep(
                        this.createUserStepsService.currentStepIndex(),
                    );
                } else {
                    this.createUserStepsService.setValidStep();
                }
            });

            onCleanup(() => sub.unsubscribe());
        });

        /**
         * Enforce 10-digit contact number limit while typing.
         */
        effect((onCleanup) => {
            const form = this.form();
            const subscriptions = ['contactNumber', 'contactPersonPhone']
                .map((fieldName) => form.get(fieldName))
                .filter((control): control is AbstractControl => Boolean(control))
                .map((control) =>
                    control.valueChanges.subscribe((value) => {
                        const rawValue = String(value ?? '');
                        const digitsOnly = rawValue.replace(/\D/g, '');
                        const limitedDigits = digitsOnly.slice(0, 10);

                        if (rawValue !== limitedDigits) {
                            control.setValue(limitedDigits);
                        }
                    }),
                );

            onCleanup(() => subscriptions.forEach((subscription) => subscription.unsubscribe()));
        });

        /**
         * Enforce password max length while typing/pasting.
         */
        effect((onCleanup) => {
            const form = this.form();
            const subscriptions = ['password', 'passwordConfirmation']
                .map((fieldName) => form.get(fieldName))
                .filter((control): control is AbstractControl => Boolean(control))
                .map((control) =>
                    control.valueChanges.subscribe((value) => {
                        const rawValue = String(value ?? '');
                        const limitedValue = rawValue.slice(
                            0,
                            InputInformationComponent.PASSWORD_MAX_LENGTH,
                        );

                        if (rawValue !== limitedValue) {
                            control.setValue(limitedValue);
                        }
                    }),
                );

            onCleanup(() => subscriptions.forEach((subscription) => subscription.unsubscribe()));
        });

        /**
         * When City is cleared (autocomplete X or value set to empty), clear derived State and Region.
         */
        effect((onCleanup) => {
            const form = this.form();
            const cityControl = form.get('city');
            if (!cityControl) return;

            const sub = cityControl.valueChanges.subscribe((val) => {
                if (val != null && val !== '') return;
                form.patchValue({ state: '', region: '' });
            });

            onCleanup(() => sub.unsubscribe());
        });

        /**
         * External signal synchronization.
         * Listen to changes in the draft service (e.g., when a user selects a new draft from the modal)
         * and update the form accordingly.
         */
        effect(() => {
            const role = this.selectedUserType()?.id;
            if (!role || role === 'none') return;

            const draft = this.createUserStepsService.getDraft(role);
            const informationData = draft?.['input-information'];

            if (informationData) {
                // We compare the current form value with the draft data to avoid redundant patches
                const currentFormValue = this.form().getRawValue();

                if (!isEqual(informationData, currentFormValue)) {
                    /**
                     * CRITICAL: We use emitEvent: false so that this patch does not trigger
                     * the 'valueChanges' effect above, which would create an infinite loop.
                     */
                    this.form().patchValue(informationData, { emitEvent: false });
                }
            }
        });
    }

    /**
     * Construct the form fields from constant file
     * @public So we can access it in the parent component `(create-user.component)`
     */
    public form = computed(() => {
        return formBuilder(
            this.fb,
            INPUT_INFORMATION_STEP_LAYOUT[
                this.selectedUserType()?.id as keyof typeof INPUT_INFORMATION_STEP_LAYOUT
            ] ?? [],
            [this.comparePasswords],
            [
                {
                    field: 'emailAddress',
                    asyncValidator: this.checkEmailAvailability(),
                },
            ],
        );
    });

    /**
     * Form fields for the dealer input information step
     */
    protected formFields = computed(() => {
        return INPUT_INFORMATION_STEP_LAYOUT[
            this.selectedUserType()?.id as keyof typeof INPUT_INFORMATION_STEP_LAYOUT
        ]?.map((fld) => ({
            placeholder: fld.placeholder,
            fieldName: fld.name,
            label: fld.label,
            type: fld.type,
            gridSpan: fld.gridSpan,
        }));
    });

    /**
     * Dealer State/Region are prefilled from City and use {@link FormField.disabled}.
     */
    protected isReadOnlyProfileLocationField(fieldName: string): boolean {
        return fieldName === 'state' || fieldName === 'region';
    }

    /**
     * Load city, and so on, for now temp only
     */
    ngOnInit(): void {
        this.loadCities();
    }

    /**
     * On image change event from avatar component
     * @param file The new updated and selected file
     */
    protected onImageChange(file: File): void {
        if (!InputInformationComponent.ALLOWED_IMAGE_MIME_TYPES.has(file.type)) {
            this.showInvalidFileModal.set(true);
            return;
        }

        this.createUserStepsService.setProfilePictureFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            const base64 = e.target?.result as string;
            this.createUserStepsService.setProfilePicturePreview(base64);
        };
        reader.readAsDataURL(file);
    }

    /**
     * Opens modal when unsupported file type is selected.
     */
    protected onInvalidFileSelected(): void {
        this.showInvalidFileModal.set(true);
    }

    /**
     * Closes invalid file selection modal.
     */
    protected closeInvalidFileModal(): void {
        this.showInvalidFileModal.set(false);
    }

    /**
     * On city search
     * @param {string} search The debounced search term
     */
    protected onSearch(search: string): void {
        const normalizedSearch = search.trim();

        if (this.lastSearchTerm() === normalizedSearch) {
            return;
        }
        this.lastSearchTerm.set(normalizedSearch);

        this.searchCitySubscriptionRef?.unsubscribe();
        
        if (!normalizedSearch) {
            this.loadCities();
            return;
        }
        this.searchCity(normalizedSearch);
    }

    /**
     * On city select, we populate the city, state and region fields automatically and patch the draft
     * @param option The selected row, or `null` when the user clears the autocomplete (state/region follow via city `valueChanges`).
     */
    protected onSelect(option: UiAutocompleteCity | null): void {
        if (!option) {
            return;
        }

        const stateValue = String(option.state ?? '').trim();

        this.form().patchValue({
            city: option.selectionKey,
            state: stateValue,
            region: option.region,
        });

        // Patch the draft
        this.createUserStepsService.patchDraftStep(
            this.selectedUserType()?.id as Exclude<UserRoles, 'none'>,
            'input-information',
            {
                city: option.selectionKey,
                state: stateValue,
                region: option.region,
            },
        );
    }

    /**
     * On input field focus
     *
     * We make the passwordConfirmation field a markAsTouched right a way so user can know when the typed password matches or not
     */
    protected onFocus(fieldName: string): void {
        if (this.form().get('password')?.value) {
            if (
                fieldName === 'passwordConfirmation' &&
                this.form().get(fieldName)?.value !== this.form().get('password')?.value
            ) {
                this.form().get(fieldName)?.markAsTouched();
                this.form().get(fieldName)?.setErrors({
                    passwordsDoNotMatch: true,
                });
            }
        }
    }

    /**
     * Prevents typing more than 10 digits in contact number fields.
     */
    protected onNumericKeydown(event: KeyboardEvent, fieldName: string): void {
        if (!this.isContactNumberField(fieldName)) return;

        const allowedControlKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'Home',
            'End',
        ];

        if (allowedControlKeys.includes(event.key)) return;
        if (event.ctrlKey || event.metaKey) return;
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
            return;
        }

        const currentValue = String(this.form().get(fieldName)?.value ?? '');
        if (currentValue.replace(/\D/g, '').length >= 10) {
            event.preventDefault();
        }
    }

    /**
     * Prevents typing more than 72 characters in password fields.
     */
    protected onPasswordKeydown(event: KeyboardEvent, fieldName: string): void {
        if (!this.isPasswordField(fieldName)) return;

        const allowedControlKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'Home',
            'End',
        ];

        if (allowedControlKeys.includes(event.key)) return;
        if (event.ctrlKey || event.metaKey) return;

        const currentValue = String(this.form().get(fieldName)?.value ?? '');
        if (currentValue.length >= InputInformationComponent.PASSWORD_MAX_LENGTH) {
            event.preventDefault();
        }
    }

    /**
     * @protected
     * @description Returns true when field should show validation errors while typing (dirty).
     * @param fieldName - The name of the form field
     * @returns boolean
     */
    protected isFieldWithLiveValidation(fieldName: string): boolean {
        return ['contactNumber', 'contactPersonPhone', 'emailAddress'].includes(fieldName);
    }

    /**
     * Returns true when field is a password input.
     */
    protected isContactNumberField(fieldName: string): boolean {
        return ['contactNumber', 'contactPersonPhone'].includes(fieldName);
    }

    protected isPasswordField(fieldName: string): boolean {
        return ['password', 'passwordConfirmation'].includes(fieldName);
    }

    /**
     * A method that saves the current form  data into the draft
     * @public So we can call it in the parent component `create-user.component`
     */
    public saveInformation(): void {
        // We override the city since since is an autocomplete object
        this.createUserStepsService.updateStep(
            this.selectedUserType()?.id as Exclude<UserRoles, 'none'>,
            'input-information' as keyof CreateUserFlowDraft[Exclude<UserRoles, 'none'>],
            this.form().getRawValue() as any,
        );
    }

    /**
     * Form validation for the input-information step for dealer user
     *
     * Triggered in the `create-user.component.ts` which is the parent component or container for all steps and sub-steps
     * @public So it can be accessed in the parent component
     */
    public validateInformationForm(): void {
        this.form().markAllAsTouched();
        this.form().updateValueAndValidity();
    }

    /**
     * Retrieves the error messages (if there is/are) for a specific field
     * @param {string} fieldName The fieldname to which we derived the error message from
     * @returns {string[]} The error messages for the specific field, returns empty when there's none
     */
    protected getFieldErrorMessage(fieldName: string): string[] {
        const control = this.form().get(fieldName);
        if (!control) return [];

        const shouldShowWhileTyping =
            this.isFieldWithLiveValidation(fieldName) && (control.dirty || control.touched);

        if (!shouldShowWhileTyping && !control.touched) return [];

        const field = INPUT_INFORMATION_STEP_LAYOUT[
            this.selectedUserType()?.id as keyof typeof INPUT_INFORMATION_STEP_LAYOUT
        ]?.find((f) => f.name === fieldName);

        if (!field?.errors) return [];

        return Object.keys(control.errors ?? {})
            .map((errorKey) => field.errors![errorKey])
            .filter(Boolean);
    }

    /**
     * Method to compare if the given pasword matches with the currently typed confirm password
     */
    private comparePasswords(control: AbstractControl): ValidationErrors | null {
        return comparePasswords(control);
    }

    /**
     * Checks whether the inputted email is taken or not
     * @returns {Observable<ValidationErrors | null>} The validation errors for the email field
     */
    private checkEmailAvailability(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            control.markAsTouched();
            const email = String(control.value ?? '').trim();

            if (!email) {
                return of(null);
            }

            // Avoid API calls while sync validators (required/pattern) are already failing.
            if (control.errors?.['pattern']) {
                return of(null);
            }

            // `of(email)` completes immediately; with `debounceTime` that still emits right away.
            // `timer` enforces an actual idle wait before calling the API.
            return timer(500).pipe(
                map(() => email),
                distinctUntilChanged(),
                switchMap((searchKey) =>
                    this.usersService
                        .getAll({
                            page: 1,
                            pageSize: 15,
                            search: searchKey,
                            searchFields: 'emailAddress',
                        })
                        .pipe(
                            map((response: ApiGetAllResponse<ApiUser>) => {
                                const normalizedEmail = searchKey.toLowerCase();
                                const isTaken = response.data.some(
                                    (user) =>
                                        user.emailAddress?.trim().toLowerCase() === normalizedEmail,
                                );
                                return isTaken ? { emailTaken: true } : null;
                            }),
                            catchError(() => of(null)),
                        ),
                ),
                first(),
            );
        };
    }

    /**
     * Maps api response to UI requirements
     * @param data - Api city response
     */
    private mapDataToUI(data: ApiCity[]): UiAutocompleteCity[] {
        return data.map((item) => {
            const displayState = (item.stateName ?? item.state)?.trim() ?? '';
            const label = displayState ? `${item.city}, ${displayState}` : item.city;
            const selectionKey = [item.city, item.state, item.region].join('|');
            return {
                city: item.city,
                country: item.country,
                state: item.state,
                stateName: item.stateName,
                zipCode: item.zipCode,
                region: item.region,
                label,
                value: selectionKey,
                selectionKey,
            };
        });
    }

    /** ========================= API calls ========================= */

    /**
     * Fetches the list of cities from city churvanesabuks mock only (TOBECHANGED)
     */
    private loadCities(): void {
        this.createUserStepsApiService
            .getCities({ limit: 50 })
            .pipe(
                withLoading(this.isLoadingCities),
                catchError((err: unknown) => {
                    Logger.error('CreateUser', 'error loading cities', err);
                    return EMPTY;
                }),
            )
            .subscribe((cities) => {
                this.cityList.set(this.mapDataToUI(cities));
            });
    }

    /**
     * Searches the list of cities, partial or complete
     * @param search - The search key for the city, partial or complete
     */
    private searchCity(search: string): void {
        this.searchCitySubscriptionRef = this.createUserStepsApiService
            .searchCity(search)
            .pipe(
                withLoading(this.isLoadingCities),
                catchError((err: unknown) => {
                    Logger.error(
                        'CreateUser',
                        'error searching cities with the provided key' + search,
                        err,
                    );
                    return EMPTY;
                }),
            )
            .subscribe((cities) => this.cityList.set(this.mapDataToUI(cities)));
    }
}
